<script setup lang="ts">
import type { Option } from '@autobid/strapi-integration/typescript/webapi/Dictionary'
import { useCategoriesToDisplay } from '@autobid/ui/composables/auction/useCategoriesToDisplay'
import { useManufacturersToDisplay } from '@autobid/ui/composables/auction/useManufacturersToDisplay'
import { useOtherFeaturesToDisplay } from '@autobid/ui/composables/auction/useOtherFeaturesToDisplay'
import { useSuppliers } from '@autobid/ui/composables/auction/useSuppliers'
import { useTypesWithCategories } from '@autobid/ui/composables/auction/useTypesWithCategories'
import { useNoFilterCounters } from '@autobid/ui/composables/car/data/useNoFilterCounters'
import { useAuction } from '@autobid/ui/composables/useAuction'
import { useAuctionId } from '@autobid/ui/composables/useAuctionId'
import { useAuctionPagesLink } from '@autobid/ui/composables/useAuctionPagesLink'
import { useDictionary } from '@autobid/ui/composables/useDictionary'
import { VEHICLE_TYPE_KEY } from '@autobid/ui/constants/VEHICLE_TYPE_KEY'
import { computed, onServerPrefetch } from 'vue'

const { data, suspense } = useNoFilterCounters('auction')
const auctionId = useAuctionId()
const { auction } = useAuction(auctionId.value)
const { dictionary } = useDictionary({ type: 'auctions' })
const { typesWithCategories } = useTypesWithCategories('auction')
const { suppliers } = useSuppliers()
const slug = computed(() => auction.value?.slug)
const { t } = useI18n()

const { getLink } = useAuctionPagesLink()
const auctionLink = computed(() => {
  if (!slug.value)
    return

  return getLink({
    page: 'car-list',
    slug: slug.value,
  })
})

const separatedTypesAndCategories = computed(() => {
  return typesWithCategories.value?.reduce((acc: Option[], currentItem) => {
    const options = currentItem.categories
    const vehicleType = dictionary.value?.[VEHICLE_TYPE_KEY].options?.find(
      item => item.id === currentItem.id,
    )

    if (!vehicleType) {
      return acc
    }

    acc.push(vehicleType)

    acc.push(...options)
    return acc
  }, [])
})

const { categoriesToDisplay } = useCategoriesToDisplay(
  separatedTypesAndCategories.value,
)
const { manufacturesToDisplay } = useManufacturersToDisplay('auction')

const { otherFeaturesToDisplay } = useOtherFeaturesToDisplay('auction')

const stagesToDisplay = computed(() => {
  return [
    data.value?.counters?.inauction
      ? {
          id: 'online',
          label: t('cars-list.filters.statuses.online'),
        }
      : null,
    data.value?.counters?.finished
      ? {
          id: 'finished',
          label: t('cars-list.filters.statuses.finished'),
        }
      : null,
  ].filter(el => el !== null)
})

function prepareTab(
  name: 'carList-category' | 'carList-manufacturerModel' | 'carList-otherFeatures' | 'carList-supplierIds' | 'carList-stage',
  label: string,
  data?: Pick<Option, 'id' | 'label'>[],
) {
  if (!data?.length)
    return null

  return {
    name: name as string,
    label,
    options: data.map((value) => {
      return {
        tabName: name as string,
        href: `${auctionLink.value}?${name}=${value.id}`,
        label: value.label,
      }
    }),
    bindParams: {
      'class': 'flex-1',
      'background-color': 'var(--color-primary-dark)',
    },
  }
}

const tabs = computed(() => {
  return [
    auctionLink.value
      ? {
          name: 'all',
          label: t('cars-list.filters.statuses.all'),
          bindParams: {
            'class': 'flex-1',
            'url': auctionLink.value,
            'background-color': 'var(--color-primary-dark)',
          },
        }
      : null,
    prepareTab(
      'carList-category',
      t('cars-list.filters.category'),
      categoriesToDisplay.value,
    ),
    prepareTab(
      'carList-manufacturerModel',
      t('cars-list.filters.manufacturer'),
      manufacturesToDisplay.value,
    ),
    otherFeaturesToDisplay.value.length
      ? {
          name: 'carList-otherFeatures',
          label: t('cars-list.filters.other-features'),
          options: otherFeaturesToDisplay.value.map((el) => {
            return {
              tabName: 'otherFeatures',
              href: `${auctionLink.value}?${el.id}`,
              label: el.label,
            }
          }),
          bindParams: {
            'class': 'flex-1',
            'background-color': 'var(--color-primary-dark)',
          },
        }
      : null,
    Object.keys(suppliers.value ?? {}).length
    && auction.value?.activeStatusesNames?.includes('show_deliverer')
      ? prepareTab(
          'carList-supplierIds',
          t('cars-list.filters.suppliers'),
          Object.values(suppliers.value),
        )
      : null,
    prepareTab(
      'carList-stage',
      t('cars-list.filters.auction-status'),
      stagesToDisplay.value,
    ),
  ].filter(el => el !== null)
})

onServerPrefetch(suspense)
await suspense()
</script>

<template>
  <ElementsButtonsNav :tabs="tabs" class="hidden lg:block" />
</template>
